import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  mutateClassCategory,
  renameClassCategory,
  sortClassCategory,
  type CategoryBulkActionRequest,
  type RenameClassCategoryRequest,
  type SortClassCategoryRequest,
} from '@/features/class/boardsApi';

function useInvalidateClassCategories() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.class.categories });
    void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
  };
}

export function useRenameClassCategoryMutation() {
  const invalidate = useInvalidateClassCategories();
  return useMutation<void, unknown, RenameClassCategoryRequest>({
    mutationKey: ['class', 'category', 'rename'],
    mutationFn: renameClassCategory,
    onSuccess: invalidate,
  });
}

export function useClassCategoryBulkMutation() {
  const invalidate = useInvalidateClassCategories();
  return useMutation<void, unknown, CategoryBulkActionRequest>({
    mutationKey: ['class', 'category', 'bulk-action'],
    mutationFn: mutateClassCategory,
    onSettled: invalidate,
  });
}

export function useSortClassCategoryMutation() {
  return useMutation<void, unknown, readonly SortClassCategoryRequest[]>({
    mutationKey: ['class', 'category', 'sort'],
    mutationFn: async (changes) => {
      // Target-order execution keeps positions stable as each move shifts the list.
      for (const change of [...changes].sort((a, b) => a.newPosition - b.newPosition)) {
        await sortClassCategory(change);
      }
    },
  });
}
